// ─── HR & payroll engine ──────────────────────────────────────────
//
// Every rule in this file is a pure function of (store, employee, pay period, today).
// Nothing reads the clock: callers pass `today` (a 'YYYY-MM-DD' PKT date — use
// nowPKTDate() from utils/auth). That is what makes the payroll reproducible: the
// figure for a past period never drifts because the date moved on, and every rule can
// be tested against a fixed day. See hr.test.js.
//
// THE MODEL (what an HR manager expects, and what the UI promises staff)
//
//  Pay period   A salary is due on `dueDay` each month and pays for the period that
//               ENDS the day before. dueDay 1 → the previous calendar month; dueDay 19
//               → 19th to 18th. A period is identified by its due month 'YYYY-MM' —
//               the same convention salary_payments.monthYear already uses.
//
//  Hours only   What counts is HOW MANY hours someone works, never WHEN. Shift start and
//               end times only set the length of a working day (10:00–19:00 = 9h); coming
//               in at 11 and leaving at 8 is a full day. Nothing here measures lateness.
//
//  Forgotten    OWNER'S RULE: someone who forgets to clock out is clocked out at their
//  clock-out    scheduled finish time (in at 11:30 on a 10–7 shift → out at 19:00, 7h30m).
//               It only happens after 23:00 that day — until then the shift is still
//               open and they can clock out themselves. The database job auto_clock_out()
//               writes it at 23:00 PKT (marked `autoOut`); for any open punch it missed,
//               this file applies the same rule from the next day on.
//
//  Expected     Scheduled minutes on every working day (Mon–Fri, not a public holiday)
//               from the later of period start / joining date. While a period is still
//               running only days up to TODAY count — a day that hasn't happened yet is
//               never a "shortfall".
//
//  Credited     Punched time on working days (any time of day — extra time counts)
//             + days an admin marked present/half with no punch at all
//             + approved overtime requests, hour for hour: on a working day the part
//               outside their clock-in; on a weekend or holiday the approved time (capped
//               at what they clocked, if they clocked in). Weekend/holiday work only
//               counts with an approved request.
//             + days off covered by the leave bank (below)
//
//  Days off     A working day not worked is a day off (half days count 0.5). Approved
//               leave, a late request, an absent mark and a day with nothing recorded
//               are all the same thing: each one is drawn from the leave bank,
//               automatically, until it runs out. Only days off beyond the bank are
//               unpaid. There is no separate "leave" that skips the bank.
//
//  Leave bank   1.5 days credited at the start of each month from 1 Jan (or the joining
//               month), cap 18, resets every 1 Jan, no carry-over. Allocated
//               chronologically through the leave year, so a period's figure only ever
//               depends on earlier periods. A period belongs to the year its midpoint
//               falls in, so the year view and the bank agree on which periods count.
//
//  Records      Days before an employee's tracking start (the app went live 4 May 2026)
//               have no punches, so they never count short hours; days off for them come
//               from the attendance grid or the owner's entry and use the bank like any
//               other day off. Salary and schedule are read
//               from per-period history, so each period is valued as it was agreed then.
//
//  Shortfall    expected − credited (so it includes unpaid days off), plus unpaid days
//               off before records. Deducted pro-rata to the full period's hours. An
//               admin can waive the deduction for a period.
//
//  Overtime     OWNER'S RULE: overtime pays only once the month's scheduled hours are
//               fulfilled. Hours up to the schedule — normal or overtime — earn the
//               salary; every hour beyond it is paid at 1.5× the hourly rate (salary ÷
//               the period's scheduled hours). 100h scheduled, 90 normal + 10 OT → salary
//               only; 90 + 20 → salary + 10h × 1.5. Each month stands alone: extra hours
//               never carry over, and a short month is never topped up by another.
// ──────────────────────────────────────────────────────────────────

export const DEFAULT_WORK_HOURS = {
  'emp1': { start: '09:00', end: '17:00' }, // Affan Khan
  'emp2': { start: '12:00', end: '19:30' }, // Hamza Ashiq
  'emp3': { start: '10:00', end: '19:00' }, // Wajiha Amir
  'emp4': { start: '09:00', end: '18:00' }, // Shoaib Ramzan
  'emp5': { start: '12:00', end: '19:30' }, // Muhammad Numan
  'emp6': { start: '10:00', end: '19:00' }, // Isha Tahir
};

export const LEAVE_DAYS_PER_MONTH = 1.5;
export const LEAVE_CAP_DAYS       = 18;
export const OT_MULTIPLIER        = 1.5;
export const LONG_SHIFT_MINS      = 14 * 60;   // flagged for a punch check, still counted
export const LEAVE_YEAR_START_MONTH = 1;       // January — the leave year is the calendar year

// ─── Dates (pure string arithmetic in UTC — immune to the machine's timezone) ──

const pad = n => String(n).padStart(2, '0');
export const ymd = (y, m, d) => `${y}-${pad(m)}-${pad(d)}`;          // m is 1–12
const split = ds => ds.split('-').map(Number);
const isDate = s => typeof s === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(s);

export function daysInMonth(y, m) { return new Date(Date.UTC(y, m, 0)).getUTCDate(); }

export function addDays(ds, n) {
  const [y, m, d] = split(ds);
  const t = new Date(Date.UTC(y, m - 1, d + n));
  return ymd(t.getUTCFullYear(), t.getUTCMonth() + 1, t.getUTCDate());
}

export function dayOfWeek(ds) { const [y, m, d] = split(ds); return new Date(Date.UTC(y, m - 1, d)).getUTCDay(); }

function shiftKey(key, n) {
  let [y, m] = key.split('-').map(Number);
  m += n;
  while (m > 12) { m -= 12; y++; }
  while (m < 1)  { m += 12; y--; }
  return `${y}-${pad(m)}`;
}
export const nextPayKey = k => shiftKey(k, 1);
export const prevPayKey = k => shiftKey(k, -1);

// Calendar month of a date, 'YYYY-MM'. Kept for month-view screens (attendance grid).
export function nowMonth(today) {
  if (isDate(today)) return today.slice(0, 7);
  const d = new Date();
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}`;
}

// ─── Time ──────────────────────────────────────────────────────────

export function toMins(t) {
  if (!t || typeof t !== 'string' || !t.includes(':')) return 0;
  const [h, m] = t.split(':').map(Number);
  return (h || 0) * 60 + (m || 0);
}

export function fmtMins(mins) {
  if (!mins || mins <= 0) return '—';
  const h = Math.floor(Math.abs(mins) / 60);
  const m = Math.round(Math.abs(mins) % 60);
  if (h === 0) return `${m}m`;
  if (m === 0) return `${h}h`;
  return `${h}h ${m}m`;
}

// Minutes in a scheduled day. An end before the start is a night shift.
export function dailyMinsOf(wh) {
  const s = toMins(wh?.start || '09:00'), e = toMins(wh?.end || '18:00');
  const span = e - s;
  return span > 0 ? span : span + 1440;
}

export function workHoursOf(store, emp) {
  const rec = (store.hr || {})[emp.id] || {};
  return rec.workHours || DEFAULT_WORK_HOURS[emp.id] || { start: '09:00', end: '18:00' };
}

// ─── History: what was agreed for a given pay period ───────────────
// salaryHistory / workHoursHistory are [{ from: 'YYYY-MM' (pay key), ... }]. The entry
// in force for a period is the latest one starting on or before it; a period before
// the first entry takes the earliest known value. No history → the current value.
const KEY_RE = /^\d{4}-\d{2}$/;
function historyAt(list, payKey) {
  const h = (Array.isArray(list) ? list : []).filter(x => x && KEY_RE.test(x.from))
    .sort((a, b) => (a.from < b.from ? -1 : a.from > b.from ? 1 : 0));
  let hit = null;
  for (const x of h) if (x.from <= payKey) hit = x;
  return hit || h[0] || null;
}
// Someone with no working hours or salary — e.g. an AI agent or a freelancer (owner,
// 2026-10-13). They get a login and work access, and are left out of payroll,
// attendance, hours, days off and the payday reminders. Flag: hr[emp].noPayroll.
export function onPayroll(store, emp) {
  return !(emp && (((store && store.hr) || {})[emp.id] || {}).noPayroll);
}

export function salaryFor(store, emp, payKey) {
  const h = historyAt((((store.hr || {})[emp.id]) || {}).salaryHistory, payKey);
  const v = h ? Number(h.amount) : NaN;
  return v > 0 ? v : (Number(emp && emp.amount) || 0);
}
export function workHoursFor(store, emp, payKey) {
  const h = historyAt((((store.hr || {})[emp.id]) || {}).workHoursHistory, payKey);
  return h && h.start && h.end ? { start: h.start, end: h.end } : workHoursOf(store, emp);
}

// First day the app holds records for this person. Before it there are no punches to
// read, so those days are never counted as missed. Set explicitly (hr.trackingStart);
// otherwise the first clock-in.
export function trackingStartOf(store, emp) {
  const rec = (store.hr || {})[emp.id] || {};
  if (isDate(rec.trackingStart)) return rec.trackingStart;
  const first = Object.keys(rec.timelog || {}).filter(isDate).sort()[0];
  return first || null;
}

// One day's punch. Never invents time: an unfinished punch is 0 and flagged.
export function punchInfo(entry) {
  const i = entry && entry.in, o = entry && entry.out;
  if (!i && !o) return { mins: 0, status: 'none' };
  if (i && !o)  return { mins: 0, status: 'missing_out' };
  if (!i && o)  return { mins: 0, status: 'missing_in' };
  let mins = toMins(o) - toMins(i);
  let overnight = false;
  if (mins < 0) { mins += 1440; overnight = true; }      // clocked out after midnight
  if (mins === 0) return { mins: 0, status: 'zero' };     // in == out: almost certainly a mis-punch
  const inM = toMins(i);
  return { mins, status: 'ok', overnight, long: mins > LONG_SHIFT_MINS, inM, outM: inM + mins };
}

// ─── Calendar ──────────────────────────────────────────────────────

function holidaySet(holidays) {
  return new Set((Array.isArray(holidays) ? holidays : []).map(h => h && h.date).filter(Boolean));
}

export function isPublicHoliday(dateStr, holidays) {
  if (!dateStr) return false;
  return holidaySet(holidays).has(dateStr);
}

export function isWorkingDay(ds, holSet) {
  const w = dayOfWeek(ds);
  return w >= 1 && w <= 5 && !holSet.has(ds);
}

// All Mon–Fri dates in a calendar month 'YYYY-MM'.
export function getWorkingDays(ym) {
  const [y, m] = ym.split('-').map(Number);
  const out = [];
  for (let d = 1; d <= daysInMonth(y, m); d++) {
    const ds = ymd(y, m, d);
    const w = dayOfWeek(ds);
    if (w >= 1 && w <= 5) out.push(ds);
  }
  return out;
}

export function getWorkingDaysExHolidays(ym, holidays) {
  const hs = holidaySet(holidays);
  return getWorkingDays(ym).filter(d => !hs.has(d));
}

// Informational month-view helpers (attendance calendar / hours card).
export function calcScheduledMins(workHours, workingDays) {
  return dailyMinsOf(workHours) * (workingDays || []).length;
}
export function calcWorkedMins(timeLog, dates) {
  return (dates || []).reduce((sum, d) => sum + punchInfo(timeLog?.[d]).mins, 0);
}
export function calcHolidayUnits(attendance, workingDays) {
  let units = 0;
  (workingDays || []).forEach(date => {
    const s = attendance?.[date];
    if (s === 'absent' || s === 'leave') units += 1;     // leave and absence are the same day off
    else if (s === 'half') units += 0.5;
  });
  return units;
}

// ─── Pay periods ───────────────────────────────────────────────────

export function dueDayOf(emp) {
  const n = Math.round(Number(emp && emp.dueDay));
  return n >= 1 && n <= 31 ? n : 1;
}

export function dueDateFor(payKey, dueDay) {
  const [y, m] = payKey.split('-').map(Number);
  return ymd(y, m, Math.min(dueDay, daysInMonth(y, m)));
}

// The period a salary due in `payKey` pays for. Periods tile with no gaps or overlaps:
// each starts on the previous due date and ends the day before this one.
export function payPeriod(emp, payKey) {
  const dd = dueDayOf(emp);
  const dueDate = dueDateFor(payKey, dd);
  return { key: payKey, start: dueDateFor(prevPayKey(payKey), dd), end: addDays(dueDate, -1), dueDate };
}

// The next salary falling due on or after `today`. On payday itself this is the
// period being paid — not the new month that has just begun.
export function upcomingPayKey(emp, today) {
  const key = today.slice(0, 7);
  return dueDateFor(key, dueDayOf(emp)) >= today ? key : nextPayKey(key);
}

export function periodStatus(period, today, paid) {
  if (paid)                      return 'paid';
  if (today < period.start)      return 'future';
  if (today <= period.end)       return 'in_progress';
  if (today === period.dueDate)  return 'due';
  return 'overdue';
}

// The 2nd-to-last working day before payday: when staff are asked whether to spend
// banked days on any shortfall.
export function decisionDateFor(dueDate, holidays) {
  const hs = holidaySet(holidays);
  const found = [];
  let d = addDays(dueDate, -1);
  for (let guard = 0; found.length < 2 && guard < 45; guard++) {
    if (isWorkingDay(d, hs)) found.push(d);
    d = addDays(d, -1);
  }
  return found[1] || found[0] || dueDate;
}

// Back-compat wrapper: (dueMonth, dueDay) → decision date for that payday.
export function payrollDecisionDate(payKey, dueDay, holidays) {
  return decisionDateFor(dueDateFor(payKey, Math.max(1, Number(dueDay) || 1)), holidays);
}

// Salary payments are recorded against the month they fall DUE (salary_payments.monthYear).
export function isPeriodPaid(store, emp, payKey) {
  return (store.salaryPayments || []).some(p => p && p.empId === emp.id && p.monthYear === payKey);
}

// Which period an admin should be looking at: the payday that most recently passed, if
// it has no recorded payment (salaries are often paid a few days late); otherwise the
// next one coming up. Older periods stay reachable with the period arrows — pointing
// at the oldest unpaid one would surface months nobody ever logged.
export function defaultAdminPayKey(store, emp, today) {
  const upcoming = upcomingPayKey(emp, today);
  const last     = prevPayKey(upcoming);            // its due date is always before today
  const joining  = joiningOf(store, emp);
  if (joining && payPeriod(emp, last).end < joining) return upcoming;
  return isPeriodPaid(store, emp, last) ? upcoming : last;
}

// ─── Leave & overtime records ──────────────────────────────────────

function joiningOf(store, emp) {
  const rec = (store.hr || {})[emp.id] || {};
  const j = rec.joiningDate || emp.joiningDate;
  return isDate(j) ? j : null;
}

// Approved leave for one employee, expanded to one entry per date. Notice period and the
// old "waive" flag don't matter for money any more — every day off uses the bank — so only
// how much of the day it covers is kept.
function leaveByDate(store, emp) {
  const map = {};
  (store.leaveRequests || []).forEach(r => {
    if (!r || r.empId !== emp.id || r.status !== 'approved' || !isDate(r.date)) return;
    const last = isDate(r.endDate) && r.endDate >= r.date ? r.endDate : r.date;
    const portion = r.type === 'half' ? 0.5 : 1;
    for (let d = r.date, guard = 0; d <= last && guard < 62; d = addDays(d, 1), guard++) {
      const prev = map[d];
      if (!prev || portion > prev.portion) {
        map[d] = { portion, id: r.id, payWith: r.payWith === 'salary' ? 'salary' : 'allowance' };
      }
    }
  });
  return map;
}

export function calcApprovedOvertimeMins(overtimeRequests, monthYear, empId) {
  let total = 0;
  (overtimeRequests || []).forEach(req => {
    if (!req || req.status !== 'approved') return;
    if (empId && req.empId !== empId) return;
    if (monthYear && !(req.date || '').startsWith(monthYear)) return;
    const mins = toMins(req.endTime) - toMins(req.startTime);
    if (mins > 0) total += mins;
  });
  return total;
}

// ─── One period, before the leave bank ─────────────────────────────

export function periodLedger(store, emp, period, today) {
  const rec     = (store.hr || {})[emp.id] || {};
  const timelog = rec.timelog || {};
  const att     = rec.attendance || {};
  const hs      = holidaySet(store.holidays);
  const wh      = workHoursFor(store, emp, period.key);
  const daily   = dailyMinsOf(wh);
  const shiftStart = toMins(wh.start || '09:00');
  const shiftEnd   = shiftStart + daily;               // past midnight for a night shift
  const joining = joiningOf(store, emp);
  const track   = trackingStartOf(store, emp);
  const leaves  = leaveByDate(store, emp);
  // How each day off is paid — the employee's choice (owner, 2026-10-12): from the leave
  // allowance (default) or as a salary cut that leaves the allowance untouched. Set per
  // day (hr.offChoice[date]) or on the leave request (payWith); the per-day choice wins.
  const offChoice = rec.offChoice || {};
  const lastDay = today < period.end ? today : period.end;
  const offDayPunch = {};                           // weekend/holiday clock-ins, by date

  const L = {
    daily, wh, joining, trackingStart: track, lastDay,
    allWorkingMins: 0,     // every working day in the period — the salary's rate base
    eligibleMins: 0,       // working days from joining onward — the salary actually earned
    expectedMins: 0,       // eligible, TRACKED working days up to today
    workedMins: 0,         // punched time, any day
    adminMins: 0,          // admin-confirmed days with no punch
    autoClosedMins: 0,     // forgotten punches closed at the scheduled boundary
    otApprovedMins: 0, otCreditMins: 0,
    workingDays: 0, daysElapsed: 0,
    holidayDays: 0,        // weekday public holidays in the period (not required)
    untrackedDays: 0,      // elapsed working days before tracking start
    untrackedOffMarks: 0,  // days off the attendance grid shows for those days
    offDays: 0,            // tracked working days not worked (half days count 0.5)
    offOnLeave: 0,         // …of which had approved leave behind them (for display only)
    offSalaryDays: 0,      // …of which the employee chose to take as a salary cut
    daysWorked: 0,
    flags: [], days: [],
  };

  for (let d = period.start, guard = 0; d <= period.end && guard < 40; d = addDays(d, 1), guard++) {
    const working  = isWorkingDay(d, hs);
    const eligible = !joining || d >= joining;
    if (working) L.allWorkingMins += daily;
    else if (hs.has(d) && dayOfWeek(d) !== 0 && dayOfWeek(d) !== 6 && eligible) L.holidayDays++;
    if (working && eligible) { L.eligibleMins += daily; L.workingDays++; }
    if (d > lastDay || !eligible) continue;

    const mark = att[d];
    if (track && d < track) {                       // no records — never a shortfall
      if (working) {
        L.untrackedDays++;
        if (mark === 'absent' || mark === 'leave') L.untrackedOffMarks += 1;
        else if (mark === 'half') L.untrackedOffMarks += 0.5;
      }
      continue;
    }
    const p  = punchInfo(timelog[d]);
    const lv = leaves[d];
    // Today isn't over. Until there's a punch, a mark or leave on it, it isn't expected
    // yet — otherwise every morning shows a full day of "shortfall".
    if (d === today && p.status === 'none' && !mark && !lv) continue;
    // Clocked in today and not out yet: the shift is still running, not a forgotten
    // punch. It's judged once it's over (or the next day, if they never clock out).
    if (d === today && p.status === 'missing_out') continue;
    if (working) { L.expectedMins += daily; L.daysElapsed++; }
    let dayMins = 0, source = null;

    const e = timelog[d] || {};
    if (p.status === 'ok' && e.autoOut && e.autoOut === e.out) {
      // Clocked out by the 23:00 job at the scheduled finish. If the owner later corrects
      // the time, autoOut no longer matches and it becomes an ordinary punch.
      dayMins = p.mins; source = 'auto_closed';
      L.autoClosedMins += dayMins;
      L.flags.push({ date: d, type: 'missing_out', mins: dayMins, at: e.out });
    } else if (p.status === 'ok' && !working) {
      // Weekend or holiday: counts only through an approved overtime request (below).
      source = 'off_day';
      offDayPunch[d] = p;
    } else if (p.status === 'ok') {
      dayMins = p.mins; source = 'punch';
      L.workedMins += dayMins;
      if (p.long) L.flags.push({ date: d, type: 'long_shift', mins: p.mins });
    } else if (p.status === 'none' && working && (mark === 'present' || mark === 'half')) {
      dayMins = mark === 'present' ? daily : Math.round(daily / 2);
      source = 'admin';
      L.adminMins += dayMins;
    } else if ((p.status === 'missing_out' || p.status === 'missing_in') && working) {
      // Forgotten clock-out: clocked out at the scheduled finish (owner's rule, see top).
      // Clocked in after the finish → nothing counted. A forgotten clock-in (the app
      // can't produce one, but an edited record can) mirrors it: in at the scheduled start.
      let inM = toMins(e.in), outM = toMins(e.out);
      if (p.status === 'missing_out') { if (shiftEnd >= 1440 && inM < shiftStart) inM += 1440; outM = shiftEnd; }
      else                            { inM = shiftStart; if (outM < inM) outM += 1440; }
      dayMins = Math.max(0, outM - inM);
      source  = 'auto_closed';
      L.autoClosedMins += dayMins;
      L.flags.push({ date: d, type: p.status, mins: dayMins, at: p.status === 'missing_out' ? fmtClock(shiftEnd) : fmtClock(shiftStart) });
    } else if (p.status !== 'none') {
      L.flags.push({ date: d, type: p.status });     // zero, or a half-punch on a day off
    }

    let off = 0, payWith = null;
    if (working) {
      // A day "off" is a working day not worked — not a short one. Whether leave was
      // approved doesn't change it: the bank pays for it either way (see walkLeaveYear).
      off = dayMins === 0 ? 1
        : (source === 'admin' && mark === 'half') || (lv && lv.portion === 0.5) ? 0.5 : 0;
      L.offDays += off;
      if (off && lv) L.offOnLeave += off;
      payWith = off ? (offChoice[d] === 'salary' || offChoice[d] === 'allowance' ? offChoice[d] : (lv ? lv.payWith : 'allowance')) : null;
      if (payWith === 'salary') L.offSalaryDays += off;
      if (dayMins > 0) L.daysWorked += off ? 1 - off : 1;
    }

    // A past working day with nothing on it at all. Today is excluded — it may not be over.
    if (working && d < today && p.status === 'none' && !source && !lv && !mark) {
      L.flags.push({ date: d, type: 'no_record' });
    }

    L.days.push({ date: d, working, punch: p.status, mins: dayMins, source, mark: mark || null, leave: lv ? lv.portion : null, off, payWith: off ? payWith : null });
  }

  // Approved overtime inside the tracked, elapsed part of the period, counted hour for
  // hour (the 1.5× is paid on the month's excess, in buildResult — never here, so it
  // can't be paid twice). Working day: only the part outside the clock-in, which is
  // already counted. Weekend/holiday: the approved time, capped at what they clocked
  // if they clocked in.
  const offDayApproved = new Set();
  (store.overtimeRequests || []).forEach(r => {
    if (!r || r.status !== 'approved' || r.empId !== emp.id || !isDate(r.date)) return;
    if (r.date < period.start || r.date > lastDay) return;
    if ((joining && r.date < joining) || (track && r.date < track)) return;
    const s = toMins(r.startTime), e = toMins(r.endTime);
    if (e <= s) return;
    const ot = e - s;
    const p = punchInfo(timelog[r.date]);
    const overlap = p.status === 'ok' ? Math.max(0, Math.min(e, p.outM) - Math.max(s, p.inM)) : 0;
    const working = isWorkingDay(r.date, hs);
    L.otApprovedMins += ot;
    L.otCreditMins   += working ? ot - overlap : (p.status === 'ok' ? overlap : ot);
    if (!working) offDayApproved.add(r.date);
  });
  Object.keys(offDayPunch).forEach(d => {
    if (!offDayApproved.has(d)) L.flags.push({ date: d, type: 'off_day_unapproved', mins: offDayPunch[d].mins });
  });

  return L;
}

// ─── Leave bank ────────────────────────────────────────────────────

export function leaveYearStart(ds) {
  const [y, m] = split(ds);
  const sm = LEAVE_YEAR_START_MONTH;
  return m >= sm ? `${y}-${pad(sm)}-01` : `${y - 1}-${pad(sm)}-01`;
}

// Days credited by `ds`: 1.5 at the start of each month from the later of the leave-year
// start and the joining month, capped. Zero before accrual begins.
export function accruedAt(ds, lyStart, joining) {
  let start = lyStart;
  if (joining && joining.slice(0, 7) + '-01' > start) start = joining.slice(0, 7) + '-01';
  if (ds < start) return 0;
  const [y1, m1] = split(start), [y2, m2] = split(ds);
  const months = (y2 - y1) * 12 + (m2 - m1) + 1;
  return Math.min(LEAVE_CAP_DAYS, LEAVE_DAYS_PER_MONTH * months);
}

// The day halfway through a period — decides which month and which year it belongs to.
export function periodMidpoint(p) {
  const [y1, m1, d1] = split(p.start), [y2, m2, d2] = split(p.end);
  const span = Math.round((Date.UTC(y2, m2 - 1, d2) - Date.UTC(y1, m1 - 1, d1)) / 86400000);
  return addDays(p.start, Math.floor(span / 2));
}

const r2 = n => Math.round(n * 100) / 100;

// Walk every pay period of the leave year in order, allocating the bank as it goes, up
// to `targetKey`. Every day off in a period — before records or tracked, approved leave
// or not — is drawn from the bank until it runs out; whatever the bank can't cover is
// unpaid. Days before tracking come first, since they are earlier in the period. With
// `collect` it returns every period, not just the target.
function walkLeaveYear(store, emp, targetKey, today, collect) {
  const target  = payPeriod(emp, targetKey);
  const lyStart = leaveYearStart(periodMidpoint(target));
  const joining = joiningOf(store, emp);
  const rec     = (store.hr || {})[emp.id] || {};
  const choices = rec.payroll || {};

  let first = targetKey;
  for (let i = 0; i < 13 && periodMidpoint(payPeriod(emp, prevPayKey(first))) >= lyStart; i++) first = prevPayKey(first);

  // Legacy lump of days taken before the app, charged to the leave year we are in now.
  let consumed = lyStart === leaveYearStart(today) ? (Number(rec.manualLeaveAdj) || 0) : 0;
  const all = [];

  for (let key = first, guard = 0; guard < 14; key = nextPayKey(key), guard++) {
    const P   = payPeriod(emp, key);
    const L   = periodLedger(store, emp, P, today);
    const acc = accruedAt(P.end < today ? P.end : today, lyStart, joining);
    const ch  = choices[key] || {};

    // Days off before tracking began: the owner's figure if entered, else the grid's.
    const offOverride  = L.untrackedDays > 0 && ch.daysOff !== undefined && ch.daysOff !== null && ch.daysOff !== '';
    const untrackedOff = L.untrackedDays > 0 ? (offOverride ? Math.max(0, Number(ch.daysOff) || 0) : L.untrackedOffMarks) : 0;
    const trackedOff   = r2(L.offDays);
    const consumedBefore = consumed;
    const available    = r2(Math.max(0, acc - consumed));

    const untrackedPaid = Math.min(available, untrackedOff);
    // Days the employee chose to take as a salary cut never touch the allowance.
    const bankable      = r2(Math.max(0, trackedOff - L.offSalaryDays));
    const trackedPaid   = Math.min(r2(available - untrackedPaid), bankable);
    const paidDays      = r2(untrackedPaid + trackedPaid);
    consumed = r2(consumed + paidDays);

    const row = { P, L, accrued: acc, available, untrackedOff, offOverride,
                  daysOff: r2(untrackedOff + trackedOff), paidDays, unpaidDays: r2(untrackedOff + trackedOff - paidDays),
                  untrackedUnpaid: r2(untrackedOff - untrackedPaid),
                  coveredMins: Math.round(trackedPaid * L.daily), bankUsed: paidDays,
                  consumedBefore, consumedAfter: consumed, remaining: r2(Math.max(0, acc - consumed)) };
    all.push(row);
    if (key === targetKey) return collect ? all : row;
  }
  return collect ? all : all[all.length - 1];
}

// Balance shown to staff today: accrued so far minus everything used through the
// period currently running.
export function leaveBalance(store, emp, today) {
  const w = walkLeaveYear(store, emp, upcomingPayKey(emp, today), today);
  const accrued = accruedAt(today, leaveYearStart(today), joiningOf(store, emp));
  return {
    accrued,
    used: w.consumedAfter,
    remaining: r2(Math.max(0, accrued - w.consumedAfter)),
    leaveYear: leaveYearStart(today),
    manualAdj: Number(((store.hr || {})[emp.id] || {}).manualLeaveAdj) || 0,
  };
}

// Back-compat name used by the employee portal.
export function calcPTO(store, empId, today) {
  const emp = (store.employees || []).find(e => e.id === empId) || { id: empId, dueDay: 1 };
  const b = leaveBalance(store, emp, today || nowPKTDateFallback());
  return { ...b, accrualYear: Number(b.leaveYear.slice(0, 4)) };
}

function nowPKTDateFallback() {
  const t = new Date(Date.now() + 5 * 3600 * 1000);       // PKT = UTC+5, no DST
  return ymd(t.getUTCFullYear(), t.getUTCMonth() + 1, t.getUTCDate());
}

export function paidFor(store, emp, payKey) {
  const rows = (store.salaryPayments || []).filter(p => p && p.empId === emp.id && p.monthYear === payKey);
  return rows.length ? rows.reduce((s, p) => s + (Number(p.amount) || 0), 0) : null;
}

// ─── THE PAYROLL ───────────────────────────────────────────────────

function buildResult(store, emp, w, today) {
  const { P, L } = w;
  const payKey   = P.key;
  const salary   = salaryFor(store, emp, payKey);
  const decision = (((store.hr || {})[emp.id] || {}).payroll || {})[payKey] || {};

  const creditedMins  = L.workedMins + L.adminMins + L.autoClosedMins + L.otCreditMins + w.coveredMins;
  const shortfallMins = Math.max(0, L.expectedMins - creditedMins);
  const surplusMins   = Math.max(0, creditedMins - L.expectedMins);
  // Unpaid days off before records have no hours to fall short of — they're added whole.
  const untrackedUnpaidMins = Math.round(w.untrackedUnpaid * L.daily);
  const netShortfallMins = shortfallMins + untrackedUnpaidMins;

  // Rate per scheduled minute across the WHOLE period, so a deduction means the same
  // money whether it is read mid-month or on payday.
  const rate = L.allWorkingMins > 0 ? salary / L.allWorkingMins : 0;
  const base = Math.round(rate * L.eligibleMins);              // full salary unless joined mid-period
  const deduction = Math.min(base, Math.round(rate * netShortfallMins));
  const waive     = !!decision.waive;
  const finalDed  = waive ? 0 : deduction;
  // Owner's rule: only hours beyond the month's schedule are overtime, paid at 1.5×.
  const overtimeMins = surplusMins;
  const overtimePay  = Math.round(OT_MULTIPLIER * rate * overtimeMins);
  const finalSalary  = Math.max(0, base - finalDed) + overtimePay;
  // ── The month as the owner reads it (2026-10-12) ──────────────────────────────────
  // Public holidays and days off are taken OUT of the hours required: a day off moves
  // the days-off tracker (allowance first) and is never ALSO counted as hours not worked.
  // Hours worked are real hours only. This is the same money as the shortfall above —
  // the cut simply splits into short hours + unpaid days off (beyond the allowance).
  const offMins         = Math.round(L.offDays * L.daily);
  const requiredMins    = Math.max(0, L.expectedMins - offMins);
  const hoursWorkedMins = L.workedMins + L.adminMins + L.autoClosedMins + L.otCreditMins;
  const hoursShortMins  = Math.max(0, requiredMins - hoursWorkedMins);
  const hoursExtraMins  = Math.max(0, hoursWorkedMins - requiredMins);
  const cutForHours     = Math.min(deduction, Math.round(rate * hoursShortMins));
  const cutForDaysOff   = deduction - cutForHours;
  const paidAmount = paidFor(store, emp, payKey);
  const paid      = paidAmount !== null;
  const tracked   = !L.trackingStart || P.end >= L.trackingStart;

  return {
    key: payKey, period: P, status: periodStatus(P, today, paid), paid, paidAmount,
    complete: today > P.end, started: today >= P.start,
    tracked, trackingStart: L.trackingStart, untrackedDays: L.untrackedDays,
    month: periodMidpoint(P).slice(0, 7),
    decisionDate: decisionDateFor(P.dueDate, store.holidays),
    wh: L.wh, dailyMins: L.daily, joining: L.joining,
    workingDays: L.workingDays, daysElapsed: L.daysElapsed,
    expectedMins: L.expectedMins, periodMins: L.eligibleMins,
    holidayDays: L.holidayDays, offMins, requiredMins, hoursWorkedMins, hoursShortMins, hoursExtraMins,
    unpaidDaysTracked: r2(Math.max(0, w.unpaidDays - w.untrackedUnpaid)), cutForHours, cutForDaysOff,
    workedMins: L.workedMins, adminMins: L.adminMins, autoClosedMins: L.autoClosedMins,
    otApprovedMins: L.otApprovedMins, otCreditMins: L.otCreditMins,
    coveredMins: w.coveredMins,
    creditedMins, effectiveWorked: creditedMins,
    shortfallMins, shortfallDays: L.daily > 0 ? r2(shortfallMins / L.daily) : 0, surplusMins,
    netMins: creditedMins - L.expectedMins,
    daysWorked: r2(L.daysWorked),
    daysOff: w.daysOff, daysOffTracked: r2(L.offDays),
    daysOffUntracked: w.untrackedOff, daysOffOverride: w.offOverride, untrackedOffMarks: L.untrackedOffMarks,
    daysOffApproved: r2(L.offOnLeave), daysOffBySalary: r2(L.offSalaryDays),
    daysOffPaid: w.paidDays, daysOffUnpaid: w.unpaidDays, untrackedUnpaidMins,
    // Average length of a day they worked (punched, admin-marked or auto-closed).
    avgDayMins: L.daysWorked > 0 ? Math.round((L.workedMins + L.adminMins + L.autoClosedMins) / L.daysWorked) : null,
    forgottenPunches: L.flags.filter(f => f.type === 'missing_out' || f.type === 'missing_in').length,
    pto: { accrued: w.accrued, available: w.available, remaining: w.remaining, used: w.consumedAfter },
    bankUsed: w.bankUsed,
    netShortfallMins, salary, base, rate, hourlyRate: Math.round(rate * 60), deduction, waive, finalDed,
    overtimeMins, overtimePay, finalSalary,
    overpaid: paid ? paidAmount - finalSalary : null,
    decided: decision.waive !== undefined,
    decidedBy: decision.decidedBy || null,
    flags: L.flags, days: L.days,
  };
}

export function calcPayroll(store, emp, payKey, today) {
  today = isDate(today) ? today : nowPKTDateFallback();
  return buildResult(store, emp, walkLeaveYear(store, emp, payKey, today), today);
}

// ─── YEAR RECORD ───────────────────────────────────────────────────
// One row per pay period of the calendar year (by midpoint — so each month appears
// once), plus totals and averages. Built from a single walk of the leave year, so every
// row's bank figure is exactly what that period's payroll uses.
export function yearRecord(store, emp, year, today) {
  today = isDate(today) ? today : nowPKTDateFallback();
  const Y = String(year);
  let last = `${Y}-12`;
  for (let k = `${Y}-10`, i = 0; i < 6; k = nextPayKey(k), i++) {
    if (periodMidpoint(payPeriod(emp, k)).slice(0, 4) === Y) last = k;
  }
  const rows = walkLeaveYear(store, emp, last, today, true)
    .map(w => buildResult(store, emp, w, today))
    .filter(r => r.month.slice(0, 4) === Y);

  const sum = (list, f) => list.reduce((a, r) => a + (Number(r[f]) || 0), 0);
  const started  = rows.filter(r => r.started);
  const tracked  = started.filter(r => r.tracked);
  const complete = tracked.filter(r => r.complete);
  const finished = started.filter(r => r.complete);
  const paidRows = rows.filter(r => r.paid && r.complete);
  const yearEnd  = `${Y}-12-31` < today ? `${Y}-12-31` : today;
  const accrued  = Y <= today.slice(0, 4) ? accruedAt(yearEnd, `${Y}-01-01`, joiningOf(store, emp)) : 0;
  // The legacy lump of days taken before the app belongs to the current leave year only.
  const lump     = Y === leaveYearStart(today).slice(0, 4) ? (Number(((store.hr || {})[emp.id] || {}).manualLeaveAdj) || 0) : 0;
  const bankUsed = r2(sum(rows, 'bankUsed') + lump);

  const totals = {
    periods: rows.length, started: started.length, tracked: tracked.length, complete: complete.length,
    expectedMins: sum(tracked, 'expectedMins'), creditedMins: sum(tracked, 'creditedMins'),
    requiredMins: sum(tracked, 'requiredMins'), hoursWorkedMins: sum(tracked, 'hoursWorkedMins'),
    hoursShortMins: sum(tracked, 'hoursShortMins'), hoursExtraMins: sum(tracked, 'hoursExtraMins'),
    workedMins: sum(tracked, 'workedMins'), netMins: sum(tracked, 'netMins'),
    // Hours balance inside a month only. Extra hours are kept on record but never carried
    // to another month, so the year shows both sides rather than one netted figure.
    extraMins: sum(tracked, 'surplusMins'), shortMins: sum(tracked, 'shortfallMins'),
    extraMonths: complete.filter(r => r.surplusMins > 0).length,
    shortMonths: complete.filter(r => r.netMins < 0).length,
    daysOff: r2(sum(started, 'daysOff')), daysOffPaid: r2(sum(started, 'daysOffPaid')),
    daysOffUnpaid: r2(sum(started, 'daysOffUnpaid')), daysWorked: r2(sum(tracked, 'daysWorked')),
    adminMins: sum(tracked, 'adminMins'), autoClosedMins: sum(tracked, 'autoClosedMins'),
    forgottenPunches: sum(tracked, 'forgottenPunches'),
    otApprovedMins: sum(tracked, 'otApprovedMins'),
    // Before-records months can owe a cut too now (days off beyond the bank), so this
    // runs over every finished month, not only tracked ones.
    shouldCut: sum(finished, 'deduction'), waived: sum(finished.filter(r => r.waive), 'deduction'),
    overtimeMins: sum(finished, 'overtimeMins'), overtimePay: sum(finished, 'overtimePay'),
    shouldPay: sum(paidRows, 'finalSalary'), paid: sum(paidRows, 'paidAmount'),
    overpaid: sum(paidRows, 'overpaid'), paidPeriods: paidRows.length,
    bank: { accrued, used: bankUsed, remaining: r2(Math.max(0, accrued - bankUsed)) },
  };
  totals.avg = {
    creditedMins: complete.length ? Math.round(sum(complete, 'creditedMins') / complete.length) : 0,
    expectedMins: complete.length ? Math.round(sum(complete, 'expectedMins') / complete.length) : 0,
    netMins:      complete.length ? Math.round(sum(complete, 'netMins') / complete.length) : 0,
    requiredMins:    complete.length ? Math.round(sum(complete, 'requiredMins') / complete.length) : 0,
    hoursWorkedMins: complete.length ? Math.round(sum(complete, 'hoursWorkedMins') / complete.length) : 0,
    daysOff:      started.length ? r2(totals.daysOff / started.length) : 0,
    // A worked day's average length, the same way a row's avgDayMins is measured.
    hoursPerDay:  totals.daysWorked ? Math.round((totals.workedMins + totals.adminMins + totals.autoClosedMins) / totals.daysWorked) : 0,
    shouldCut:    finished.length ? Math.round(totals.shouldCut / finished.length) : 0,
  };
  return { year: Number(Y), rows, totals };
}

export function fmtClock(mins) {
  if (mins === null || mins === undefined) return '—';
  const m = ((Math.round(mins) % 1440) + 1440) % 1440;
  return `${pad(Math.floor(m / 60))}:${pad(m % 60)}`;
}

// ─── Display (shared by the HR page and the employee portal) ───────

export function fmtDate(ds) {
  if (!isDate(ds)) return '';
  const [y, m, d] = split(ds);
  const mon = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'][m - 1];
  return `${d} ${mon}${y !== Number(nowPKTDateFallback().slice(0, 4)) ? ' ' + y : ''}`;
}
export function fmtPeriod(p) { return p ? `${fmtDate(p.start)} – ${fmtDate(p.end)}` : ''; }

export const PERIOD_STATUS = {
  paid:        { label: 'Paid',        color: 'green'  },
  due:         { label: 'Due today',   color: 'orange' },
  overdue:     { label: 'Overdue',     color: 'red'    },
  in_progress: { label: 'In progress', color: 'blue'   },
  future:      { label: 'Not started', color: 'gray'   },
};

// Plain-English explanation of each ledger flag, for the "needs attention" list.
export function flagText(f) {
  switch (f.type) {
    case 'missing_out':  return f.mins != null ? (f.mins > 0 ? `Forgot to clock out — clocked out automatically at ${f.at} (${fmtMins(f.mins)} counted)` : `Forgot to clock out, and clocked in after the ${f.at} finish — nothing counted`) : 'Clocked in on a day off and never out — nothing counted';
    case 'missing_in':   return f.mins != null ? `No clock-in — counted from the ${f.at} start (${fmtMins(f.mins)})` : 'Clocked out on a day off with no clock-in — nothing counted';
    case 'no_record':    return 'No punch, leave or attendance mark — counted as a day off';
    case 'zero':         return 'Clock-in and clock-out are the same time — nothing counted';
    case 'long_shift':   return `Unusually long shift (${fmtMins(f.mins)}) — worth checking the punch`;
    case 'off_day_unapproved': return `Clocked ${fmtMins(f.mins)} on a weekend or holiday with no approved overtime request — not counted`;
    default:             return f.type;
  }
}
// Flags that change money and want a human to look.
export const FLAG_NEEDS_ACTION = new Set(['missing_out', 'missing_in', 'no_record', 'zero', 'long_shift', 'off_day_unapproved']);
